// pixelsPressure.js
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';

const defaultBrushSettings = brushDefaults.pixels || {};

export async function drawPixelBrushStroke(stroke, context) {

  const { points, color, lineWidth, sets = {} } = stroke;
  const brushSettings = { ...defaultBrushSettings, ...sets };

  const {
    pixelSize = 10,
    borderWidth = 1,
    borderColor = '#000000',
    outsideBorders = false,
  } = brushSettings;

  if (!points || points.length === 0 || !pixelSize) return;

  // Проверяем, есть ли данные о нажатии в первой точке
  const hasPressure = points[0] && points[0].pressure !== undefined;

  // Интерполируем точки, чтобы получить плавную линию пикселей
  const interpolatedPoints = [];
  if (points.length > 1) {
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      
      const distance = Math.sqrt(dx * dx + dy * dy);
      // Шаг интерполяции зависит от размера пикселя для плотного покрытия
      const steps = Math.max(1, Math.ceil(distance / (pixelSize / 2)));

      for (let j = 0; j < steps; j++) {
        const t = j / steps;
        const newPoint = {
          x: p1.x + dx * t,
          y: p1.y + dy * t,
        };
        // Также интерполируем значение нажатия, если оно есть
        if (hasPressure) {
            const p1Pressure = p1.pressure === undefined ? 1.0 : p1.pressure;
            const p2Pressure = p2.pressure === undefined ? 1.0 : p2.pressure;
            newPoint.pressure = p1Pressure + (p2Pressure - p1Pressure) * t;
        }
        interpolatedPoints.push(newPoint);
      }
    }
    interpolatedPoints.push(points[points.length - 1]);
  } else {
    interpolatedPoints.push(...points);
  }

  // Функция для создания уникального ключа пикселя
  const getPixelKey = (x, y) => `${x},${y}`;

  // Собираем все пиксели для отрисовки, сохраняя максимальное значение нажатия для каждого
  const pixelsToDraw = new Map();
  const radius = Math.max(lineWidth / 2 - 1, 0.5);
  const radiusSq = radius * radius;

  for (const point of interpolatedPoints) {
    const currentPressure = point.pressure === undefined ? 1.0 : point.pressure;
    
    // Определяем область сетки, которую затрагивает кисть
    const startGridX = Math.floor((point.x - radius) / pixelSize);
    const endGridX = Math.ceil((point.x + radius) / pixelSize);
    const startGridY = Math.floor((point.y - radius) / pixelSize);
    const endGridY = Math.ceil((point.y + radius) / pixelSize);

    // Проверяем каждую ячейку сетки на пересечение с кистью
    for (let gridX = startGridX; gridX < endGridX; gridX++) {
      for (let gridY = startGridY; gridY < endGridY; gridY++) {
        
        const rectX = gridX * pixelSize;
        const rectY = gridY * pixelSize;

        // Находим ближайшую точку на прямоугольнике к центру круга
        const closestX = Math.max(rectX, Math.min(point.x, rectX + pixelSize));
        const closestY = Math.max(rectY, Math.min(point.y, rectY + pixelSize));

        const distanceX = point.x - closestX;
        const distanceY = point.y - closestY;
        const distanceSquared = (distanceX * distanceX) + (distanceY * distanceY);

        // Если расстояние меньше радиуса, значит пиксель нужно закрасить
        if (distanceSquared < radiusSq) {
          const pixelKey = getPixelKey(gridX, gridY);
          const existing = pixelsToDraw.get(pixelKey);

          if (existing) {
            // Если пиксель уже есть, обновляем его нажатие, если новое значение больше
            existing.pressure = Math.max(existing.pressure, currentPressure);
          } else {
            pixelsToDraw.set(pixelKey, { x: gridX, y: gridY, pressure: currentPressure });
          }
        }
      }
    }
  }

  // 1. Закрашиваем все пиксели, учитывая нажатие для определения непрозрачности
  const baseChromaColor = chroma(color);
  const originalAlpha = baseChromaColor.alpha();

  pixelsToDraw.forEach(pixel => {
    const pixelX = pixel.x * pixelSize;
    const pixelY = pixel.y * pixelSize;

    if (hasPressure) {
      // Если есть данные о нажатии, вычисляем итоговую непрозрачность
      const finalAlpha = originalAlpha * pixel.pressure;
      context.fillStyle = baseChromaColor.alpha(finalAlpha).hex();
    } else {
      // Иначе используем исходный цвет
      context.fillStyle = color;
    }
    
    context.fillRect(pixelX, pixelY, pixelSize, pixelSize);
  });

  // 2. Рисуем границы поверх пикселей
  if (borderWidth > 0) {
    context.strokeStyle = borderColor;
    context.lineWidth = borderWidth;

    if (outsideBorders) {
      // Рисуем только внешние границы
      context.beginPath();
      pixelsToDraw.forEach(pixel => {
        const { x: gridX, y: gridY } = pixel;
        const pixelX = gridX * pixelSize;
        const pixelY = gridY * pixelSize;

        // Проверяем наличие соседних пикселей
        const hasTop = pixelsToDraw.has(getPixelKey(gridX, gridY - 1));
        const hasBottom = pixelsToDraw.has(getPixelKey(gridX, gridY + 1));
        const hasLeft = pixelsToDraw.has(getPixelKey(gridX - 1, gridY));
        const hasRight = pixelsToDraw.has(getPixelKey(gridX + 1, gridY));

        // Рисуем линию, если соседа нет
        if (!hasTop) {
          context.moveTo(pixelX, pixelY);
          context.lineTo(pixelX + pixelSize, pixelY);
        }
        if (!hasBottom) {
          context.moveTo(pixelX, pixelY + pixelSize);
          context.lineTo(pixelX + pixelSize, pixelY + pixelSize);
        }
        if (!hasLeft) {
          context.moveTo(pixelX, pixelY);
          context.lineTo(pixelX, pixelY + pixelSize);
        }
        if (!hasRight) {
          context.moveTo(pixelX + pixelSize, pixelY);
          context.lineTo(pixelX + pixelSize, pixelY + pixelSize);
        }
      });
      context.stroke();
    } else {
      // Рисуем сетку для всех пикселей
      pixelsToDraw.forEach(pixel => {
        const pixelX = pixel.x * pixelSize;
        const pixelY = pixel.y * pixelSize;
        context.strokeRect(pixelX, pixelY, pixelSize, pixelSize);
      });
    }
  }
}